import React, { useRef } from 'react';
import { Spinner } from '@heroui/react';
import { CustomButton } from '@components/ui/button/CustomButton';
import { AlertCircleIcon } from '@assets/icons/AlertCircleIcon';
import { useLegalSdkMount } from '@hooks/verification/useLegalSdkMount';
import { getLegalSdkSupport } from '@utils/legalSdk';
import { cn } from '@utils/cn';
import { PANEL_VERIFICATION_CARD_CLASSES } from './panelVerificationCard';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface LegalVerificationSdkProps {
  newVerification: boolean;
  onStatusChange: (status: string) => void;
  onComplete: () => void;
  onAlreadyActive: () => void;
  onNewVerificationRejected: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Contenedor del SDK de Legal. Montarlo inicia el trámite (o lo retoma) y
 * desmontarlo —al cerrar el modal— llama a `handle.unmount()`. El estilo del
 * SDK vive en su shadow DOM, y trae su propia tarjeta: el contenedor no le pone
 * superficie. Carga y error sí la llevan, porque se ven en su lugar.
 */
export const LegalVerificationSdk: React.FC<LegalVerificationSdkProps> = ({
  newVerification,
  onStatusChange,
  onComplete,
  onAlreadyActive,
  onNewVerificationRejected,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { state, error, retry } = useLegalSdkMount({
    containerRef,
    newVerification,
    onStatusChange,
    onComplete,
    onAlreadyActive,
    onNewVerificationRejected,
  });
  const support = getLegalSdkSupport();

  return (
    <div className="relative">
      {/* Siempre en el DOM: el SDK se monta aquí en cuanto hay script y token.
          Hasta que termina su propia carga queda transparente debajo de la
          nuestra (no `hidden`: el SDK enfoca su título al dibujarlo), así se
          ve una sola carga y el modal cambia de tamaño una sola vez. */}
      <div
        ref={containerRef}
        aria-hidden={state !== 'ready' || undefined}
        className={cn(
          state !== 'ready' &&
            'pointer-events-none absolute inset-x-0 top-0 opacity-0',
        )}
        data-testid="legal-sdk-container"
      />

      {(state === 'loading' || state === 'mounting') && (
        <div
          role="status"
          className={cn(
            PANEL_VERIFICATION_CARD_CLASSES,
            'flex min-h-80 flex-col items-center justify-center gap-3 p-6',
          )}
        >
          <Spinner size="lg" color="primary" />
          <span className="text-body-2 text-fg-secondary">
            Cargando la verificación…
          </span>
        </div>
      )}

      {state === 'error' && error && (
        <div
          role="alert"
          className={cn(
            PANEL_VERIFICATION_CARD_CLASSES,
            'flex min-h-80 flex-col items-center justify-center gap-4 p-6 text-center',
          )}
        >
          <AlertCircleIcon
            className="size-10 shrink-0 text-icon-error"
            aria-hidden
          />
          <p className="max-w-sm text-body-2 text-fg-secondary">
            {error.message}
          </p>
          {error.kind === 'retryable' && (
            <CustomButton label="Reintentar" onPress={retry} />
          )}
          {error.kind === 'data_mismatch' && support?.url && (
            <CustomButton
              label="Contactar a soporte"
              as="a"
              href={support.url}
              target="_blank"
              rel="noopener noreferrer"
            />
          )}
        </div>
      )}
    </div>
  );
};
